import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { http, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, CreditCard, MapPin, Calendar, Sparkle } from "@phosphor-icons/react";
import ScheduleSection from "@/pages/ScheduleSection";
import RatingWidget from "@/components/RatingWidget";

const STATUS_LABEL = {
    pending_review: "Pending Butler review",
    quoted: "Quote ready",
    approved: "Approved — awaiting payment",
    in_progress: "In progress",
    completed: "Completed",
    paid: "Paid",
    cancelled: "Cancelled",
};

export default function TaskDetailPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const [task, setTask] = useState(null);
    const [error, setError] = useState("");
    const [acting, setActing] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await http.get(`/tasks/${id}`);
            setTask(data);
        } catch (e) { setError(formatApiError(e)); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    async function approve() {
        setActing(true); setError("");
        try { await http.post(`/tasks/${id}/approve`); await load(); }
        catch (e) { setError(formatApiError(e)); }
        finally { setActing(false); }
    }

    async function pay() {
        setActing(true); setError("");
        try {
            const { data } = await http.post(`/payments/checkout`, {
                task_id: id,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) { setError(formatApiError(e)); setActing(false); }
    }

    if (!task) {
        return <div className="max-w-5xl mx-auto px-6 py-16 text-slate-500">{error || "Loading…"}</div>;
    }

    const quoteAmount = task.final_amount ?? task.butler_quote_amount ?? task.ai_quote_amount;
    const canApprove = !!(task.butler_quote_amount || task.ai_quote_amount) && task.status !== "approved" && task.status !== "paid" && task.status !== "in_progress" && task.status !== "completed";
    const canPay = (task.status === "approved" || task.status === "in_progress" || task.status === "completed") && task.payment_status !== "paid";

    return (
        <div data-testid="task-detail-page" className="max-w-6xl mx-auto px-6 sm:px-10 py-12">
            <Link to="/dashboard" className="text-sm text-slate-400 hover:text-white">← Back</Link>
            <div className="grid lg:grid-cols-12 gap-10 mt-6">
                <div className="lg:col-span-7">
                    <div className="label-eyebrow mb-3">/ {task.category}</div>
                    <h1 className="font-serif text-4xl sm:text-5xl leading-tight">{task.title}</h1>
                    <div className="mt-4 text-gold text-sm">{STATUS_LABEL[task.status] || task.status}</div>

                    <p className="mt-8 text-slate-300 leading-relaxed whitespace-pre-line">{task.description}</p>

                    {task.ai_project_summary && (
                        <div className="mt-6 bg-slate-900/50 border-l-2 border-gold/40 pl-4 py-3 text-sm text-slate-300 italic">
                            <div className="label-eyebrow text-gold mb-1">/ Concierge summary</div>
                            {task.ai_project_summary}
                        </div>
                    )}

                    <div className="mt-8 grid sm:grid-cols-2 gap-4">
                        <div className="bg-slate-900/50 border border-white/5 p-4 rounded-sm flex items-start gap-3">
                            <MapPin size={20} weight="duotone" className="text-gold mt-0.5" />
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-500">Address</div>
                                <div className="text-sm mt-1">{task.address}</div>
                            </div>
                        </div>
                        {task.preferred_date && (
                            <div className="bg-slate-900/50 border border-white/5 p-4 rounded-sm flex items-start gap-3">
                                <Calendar size={20} weight="duotone" className="text-gold mt-0.5" />
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-slate-500">Preferred date</div>
                                    <div className="text-sm mt-1">{task.preferred_date}</div>
                                </div>
                            </div>
                        )}
                    </div>

                    {task.butler_notes && (
                        <div className="mt-6 bg-gold/5 border border-gold/20 p-5 rounded-sm">
                            <div className="label-eyebrow text-gold mb-2">/ Note from your Butler</div>
                            <div className="text-sm text-slate-200">{task.butler_notes}</div>
                        </div>
                    )}

                    {task.photos && task.photos.length > 0 && (
                        <div className="mt-8">
                            <div className="label-eyebrow mb-3">/ Photos</div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {task.photos.map((p, i) => {
                                    const token = localStorage.getItem("br_token");
                                    const src = `${API}/files/${p}?auth=${encodeURIComponent(token || "")}`;
                                    return (
                                        <a key={p + i} href={src} target="_blank" rel="noreferrer" className="block aspect-square bg-slate-950 border border-white/10 rounded-sm overflow-hidden hover:border-gold transition">
                                            <img src={src} alt="" className="w-full h-full object-cover" />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {task.lead_status === "claimed" && (
                        <>
                            {task.contractor && (
                                <div data-testid="contractor-card" className="mt-8 rounded-sm border border-emerald-500/30 bg-emerald-500/5 p-5">
                                    <div className="label-eyebrow text-emerald-300 mb-2">/ Your contractor</div>
                                    <div className="font-serif text-xl">{task.contractor.name}</div>
                                    {task.contractor.business_name && (
                                        <div className="text-sm text-slate-300 mt-0.5">{task.contractor.business_name}</div>
                                    )}
                                    {task.contractor.phone && (
                                        <div className="text-sm text-slate-300 mt-2">📞 {task.contractor.phone}</div>
                                    )}
                                </div>
                            )}
                            <ScheduleSection
                                taskId={id}
                                role={user?.role || "client"}
                                task={task}
                                onUpdated={load}
                            />
                        </>
                    )}

                    {/* Rating widget — only for the client, only after payment, only when contractor was assigned */}
                    {task.payment_status === "paid"
                        && task.lead_claimed_partner_id
                        && (!user || user.role !== "partner")
                        && (
                            <RatingWidget task={task} onRated={load} />
                        )}
                </div>

                <aside className="lg:col-span-5">
                    <div className="bg-slate-900/70 border border-white/10 p-8 rounded-sm sticky top-24">
                        <div className="label-eyebrow mb-3">
                            {task.butler_quote_amount ? "/ Butler's Quote" : "/ AI Quote"}
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="font-serif text-5xl gold-text">${quoteAmount?.toFixed(2)}</span>
                            <span className="text-xs text-slate-500 uppercase tracking-widest">USD</span>
                        </div>

                        {task.ai_quote_amount && task.butler_quote_amount && task.ai_quote_amount !== task.butler_quote_amount && (
                            <div className="mt-2 text-xs text-slate-500">
                                AI estimate was <s>${task.ai_quote_amount.toFixed(2)}</s>
                            </div>
                        )}

                        <div className="mt-6 text-sm text-slate-400 italic flex items-start gap-2">
                            <Sparkle size={14} weight="duotone" className="text-gold mt-1 shrink-0" />
                            <span>{task.ai_quote_reasoning}</span>
                        </div>

                        {task.ai_base_value && task.ai_multiplier && (
                            <div data-testid="quote-breakdown" className="mt-6 bg-slate-950/40 border border-white/5 rounded-sm p-4 text-xs">
                                <div className="label-eyebrow text-slate-500 mb-3">/ Breakdown</div>
                                <div className="flex justify-between py-1.5 border-b border-white/5">
                                    <span className="text-slate-400">Value tier</span>
                                    <span className="text-slate-200">{task.ai_value_tier_label}</span>
                                </div>
                                <div className="flex justify-between py-1.5 border-b border-white/5">
                                    <span className="text-slate-400">Base value</span>
                                    <span className="text-slate-200">${task.ai_base_value.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between py-1.5 border-b border-white/5">
                                    <span className="text-slate-400">Complexity</span>
                                    <span className="text-slate-200">{task.ai_complexity_label} × {task.ai_multiplier.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between py-1.5 mt-1">
                                    <span className="text-slate-400">Estimated hours</span>
                                    <span className="text-slate-200">{task.ai_estimated_hours}</span>
                                </div>
                                <div className="mt-2 pt-2 border-t border-gold/20 text-center text-slate-500">
                                    ${task.ai_base_value.toFixed(2)} × {task.ai_multiplier.toFixed(2)} = <span className="text-gold">${(task.ai_base_value * task.ai_multiplier).toFixed(2)}</span>
                                </div>
                            </div>
                        )}

                        {error && <div className="mt-4 text-sm text-red-400">{error}</div>}

                        <div className="mt-8 space-y-3">
                            {canApprove && (
                                <button
                                    data-testid="approve-quote-btn"
                                    onClick={approve} disabled={acting}
                                    className="btn-gold w-full disabled:opacity-50"
                                >
                                    <CheckCircle size={16} weight="fill" className="inline mr-2" /> Approve quote
                                </button>
                            )}
                            {canPay && (
                                <button
                                    data-testid="pay-now-btn"
                                    onClick={pay} disabled={acting}
                                    className="btn-gold w-full disabled:opacity-50"
                                >
                                    <CreditCard size={16} weight="fill" className="inline mr-2" />
                                    {acting ? "Redirecting…" : `Pay $${quoteAmount?.toFixed(2)}`}
                                </button>
                            )}
                            {task.payment_status === "paid" && (
                                <div className="text-sm text-emerald-300 text-center py-3 border border-emerald-500/30 rounded-sm bg-emerald-500/10">
                                    ✓ Paid in full
                                </div>
                            )}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}
